import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyNavigationEffectValue,
  resetNavigationEffectValue,
} from "../shared/page-navigation-effects.ts";

const attribute = "data-migrosone-a11y-large-cursor";
const cursorVariables = [
  "--migrosone-a11y-large-cursor",
  "--migrosone-a11y-large-cursor-pointer",
] as const;
const allowedSizes = ["large", "extra-large"] as const;

type CursorSize = (typeof allowedSizes)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readSize(config: unknown): CursorSize {
  const configuredSize =
    isRecord(config) && typeof config.size === "string" ? config.size.trim() : "large";

  return allowedSizes.includes(configuredSize as CursorSize)
    ? (configuredSize as CursorSize)
    : "large";
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function cursorSvg(size: CursorSize, fill: string) {
  const dimension = size === "extra-large" ? 32 : 28;
  const path = "M3 2 L24 17 L15 19 L20 30 L15 32 L10 21 L3 27 Z";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${dimension}" height="${dimension}" viewBox="0 0 32 32"><path d="${path}" fill="${fill}" stroke="white" stroke-width="2"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function cursorValue(size: CursorSize, fill: string, fallback: "default" | "pointer") {
  return `url("${cursorSvg(size, fill)}") 0 0, ${fallback}`;
}

function resetCursorVariables(document: Document) {
  for (const variable of cursorVariables) {
    resetNavigationEffectValue({ attribute, document, variable });
  }
}

function applyCursorStyle(document: Document, size: CursorSize) {
  applyNavigationEffectValue({
    attribute,
    document,
    value: cursorValue(size, "#111827", "default"),
    variable: "--migrosone-a11y-large-cursor",
  });
  applyNavigationEffectValue({
    attribute,
    document,
    value: cursorValue(size, "#1f6f4a", "pointer"),
    variable: "--migrosone-a11y-large-cursor-pointer",
  });
}

const definition = getFeaturePanelDefinition("large-cursor");

if (!definition) {
  throw new Error("Missing large-cursor feature definition");
}

export const largeCursorFeatureModule: RuntimeFeatureModule = {
  key: "large-cursor",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Büyük imleç";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetCursorVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyCursorStyle(document, readSize(config));
  },
  reset({ config, document }) {
    resetCursorVariables(document);
    if (readBooleanDefault(config)) {
      applyCursorStyle(document, readSize(config));
    }
  },
};
